import { LiliTextInput, Text } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { ControllerWrapper } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index.ts';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@Services/utils/utils.ts';
import { Line1AddressInput } from '@Components/inputs/Line1Autocomplete/Line1AddressInput';
import { AddressStateInput } from '@Components/inputs/AddressStateDropDown';
import { ZipInput } from '@Components/inputs/ZipAddress';
import { CountryInput } from '@Components/inputs/AddressCountryAutoComplete/CountryInput';
import type { LiliDropdownItem } from '@lili/lili-frontend-common';
import type { Country } from '@Types/country';
export interface IHomeAddressFormProps {
  disabled: boolean;
  countries: Country[] | undefined;
  states: LiliDropdownItem[] | undefined;
  selectedCountry: Country | null | undefined;
  isUS: boolean;
  hideNonUsNote?: boolean;
}

export const HomeAddressForm = ({
  disabled,
  countries,
  states,
  selectedCountry,
  isUS,
  hideNonUsNote,
}: IHomeAddressFormProps) => {
  const { t } = useTranslation();
  const baseTabIndex = getRouteOrdinal(RoutesMap.HOME_ADDRESS);
  const countryList = countries?.map((country) => ({
    title: country.name,
    value: country.code,
    ...country,
  }));
  const isChildrenOfCountryShouldBeDisabled = (countries && !selectedCountry) || false;
  const hideStates = !isUS && !selectedCountry?.subDivisions?.length;

  return (
    <div className='w-full h-full flex flex-col justify-between'>
      <div className='w-full'>
        <Line1AddressInput
          disabled={disabled}
          tabIndex={baseTabIndex}
          isUsResident={isUS}
          validStates={states}
        />

        <div className='flex flex-col items-start w-full'>
          <div className='w-full'>
            <ControllerWrapper name={OnboardingFormFields.APARTMENT}>
              {({ value, onBlur, onChange, error }) => (
                <LiliTextInput
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  id={OnboardingFormFields.APARTMENT}
                  title={t('pages.homeAddress.lineTwoFieldTitle')}
                  errorMessage={error?.message}
                  tabIndex={baseTabIndex + 1}
                  disabled={disabled}
                  isPublic
                />
              )}
            </ControllerWrapper>
          </div>

          <div className='w-full'>
            <ControllerWrapper name={OnboardingFormFields.CITY}>
              {({ value, onBlur, onChange, error }) => (
                <LiliTextInput
                  value={value}
                  onBlur={() => {
                    onChange(normalizeInputSpacesOnBlur(value));
                    onBlur();
                  }}
                  onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
                  id={OnboardingFormFields.CITY}
                  title={t('pages.homeAddress.cityFieldTitle')}
                  errorMessage={error?.message}
                  disabled={disabled}
                  tabIndex={baseTabIndex + 2}
                  isPublic
                />
              )}
            </ControllerWrapper>
          </div>

          <div className='w-full' data-testid='country_input_wrapper'>
            {countryList && <CountryInput tabIndex={baseTabIndex + 3} countryList={countryList} />}
          </div>
        </div>

        <div className='w-full flex sm:flex-row flex-col sm:gap-[16px]'>
          {!hideStates && (
            <div className='flex-1' data-testid='state_dropdown_wrapper'>
              <AddressStateInput
                disabled={disabled || isChildrenOfCountryShouldBeDisabled}
                tabIndex={baseTabIndex + 4}
                statesList={states}
              />
            </div>
          )}

          <div className={!hideStates ? 'flex-1' : 'w-full'}>
            <ZipInput
              disabled={disabled || isChildrenOfCountryShouldBeDisabled}
              tabIndex={baseTabIndex + 5}
              isUS={isUS}
            />
          </div>
        </div>
      </div>

      {!isUS && !hideNonUsNote && (
        <div className='w-full sm:pb-0 pb-[20px]'>
          <Text.H10 className='text-text-main-tertiary text-start'>
            {t('pages.homeAddress.nonUsVerificationInfo')}
          </Text.H10>
        </div>
      )}
    </div>
  );
};
